<template>
  <div>
    <b-row class="mb-3 mt-3">
      <b-col cols="4">
        <strong>Tímabil</strong>
        <b-form-select id="period" name="period" v-model="search.period" @change="onSearchChanged">
          <option :value="null" v-if="!autoPeriod">-- Veldu tímabil --</option>
          <template v-if="!periodsLoading && periods.length > 0">
            <option v-for="period in periods" :key="period.id" :value="period.id">
              {{ period.name }}
            </option>
          </template>
        </b-form-select>
      </b-col>
      <b-col cols="4">
        <template v-if="!periodsLoading && periodParts.length > 0">
          <strong> Starfstímabil </strong>
          <b-form-select id="periodPart" name="periodPart" v-model="search.periodPart" @change="onSearchChanged">
            <option :value="null">
              <span>-- Veldu starfstímabil --</span>
            </option>
            <option v-for="part in periodParts" :key="part.id" :value="part.id">
              {{ part.date_from | moment('DD.MM.YYYY') }} - {{ part.date_to | moment('DD.MM.YYYY') }}
            </option>
          </b-form-select>
        </template>
      </b-col>
    </b-row>
    <b-row class="mb-3">
      <b-col cols="4">
        <strong>Flokkun starfsstaða</strong>
        <b-form-select id="type" name="type" v-model="search.workplaceType" @change="onSearchChanged">
          <option :value="null">-- Veldu flokk --</option>
          <template v-if="!workplaceTypesLoading && workplaceTypes.length > 0">
            <option v-for="type in workplaceTypes" :key="type.id" :value="type.id">
              {{ type.name }}
            </option>
          </template>
        </b-form-select>
      </b-col>
      <b-col cols="4">
        <strong>Starfsstaður</strong>
        <b-form-select id="workplace" name="workplace" v-model="search.workplace" @change="onSearchChanged">
          <option :value="null">-- Veldu starfsstað --</option>
          <template v-if="!workplacesLoading && workplaces.length > 0">
            <option v-for="workplace in workplaces" :key="workplace.id" :value="workplace.id">
              {{ workplace.name }}
            </option>
          </template>
        </b-form-select>
      </b-col>
    </b-row>
    <b-row class="mt-3">
      <b-col cols="4">
        <strong>Hópur</strong>
        <b-form-select id="placementGroup" name="placementGroup" v-model="search.placementGroup" @change="onSearchChanged">
          <option :value="null">-- Veldu hóp --</option>
          <template v-if="!placementGroupsLoading && placementGroups.length > 0">
            <option v-for="placementGroup in placementGroups" :key="placementGroup.id" :value="placementGroup.id">
              {{ placementGroup.name }}
              {{ placementGroup.date_from | moment('DD.MM.YYYY') }} -
              {{ placementGroup.date_to | moment('DD.MM.YYYY') }}
            </option>
          </template>
        </b-form-select>
      </b-col>
      <b-col cols="4" v-if="kjarni">
        <strong>Til í launakerfi</strong>
        <b-form-select id="exists" name="exists" v-model="search.exists" @change="onSearchChanged">
          <option :value="null">Allir</option>
          <option value="false">Ekki til í launakerfi</option>
          <option value="true">Til í launakerfi</option>
          <option value="sent-yes">Sendir í launakerfi</option>
          <option value="sent-no">Ekki sendir í launakerfi</option>
        </b-form-select>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import core from '@/api/core';

export default {
  name: 'payroll-employee-filters',
  props: {
    value: Object,
    databaseId: Boolean,
    autoPeriod: Boolean,
    kjarni: Boolean,
  },
  data() {
    return {
      search: {
        placementGroup: null,
        period: null,
        periodPart: null,
        workplace: null,
        workplaceType: null,
        exists: null,
      },
      periods: [],
      workplaceTypes: [],
      workplaces: [],
      placementGroups: [],
      periodsLoading: false,
      workplaceTypesLoading: false,
      workplacesLoading: false,
      placementGroupsLoading: false,
    };
  },
  computed: {
    periodParts() {
      if (!this.search.period) return [];
      const period = this.periods.find(p => p.id === this.search.period);
      return period ? period.parts || [] : [];
    },
  },
  watch: {
    'search.workplaceType': function () {
      this.search.workplace = null;
      this.loadWorkplaces();
    },
    'search.period': function () {
      if (!this.search.period) {
        this.search.periodPart = null;
      } else if (this.search.periodPart) {
        if (!this.periodParts.find(p => p.id === this.search.periodPart)) {
          this.search.periodPart = null;
        }
      }
      this.loadPlacementGroups();
    },
    'search.workplace': function () {
      this.loadPlacementGroups();
    },
  },
  async created() {
    await Promise.all([this.loadPeriods(), this.loadWorkplaceTypes()]);
  },
  methods: {
    async loadPeriods() {
      this.periodsLoading = true;
      try {
        const response = await core.dropdownPeriodList();
        this.periods = response.data;
        if (this.autoPeriod && this.periods.length > 0 && !this.search.period) {
          this.search.period = this.periods[0].id;
          this.onSearchChanged();
        }
      } catch (e) {
        this.$log.error(e);
        this.displayError(`Óvænt villa kom upp. Vinsamlegast reyndu aftur.`);
      } finally {
        this.periodsLoading = false;
      }
    },
    async loadWorkplaceTypes() {
      this.workplaceTypesLoading = true;
      try {
        const response = await core.dropdownWorkplacetypeList();
        this.workplaceTypes = response.data;
      } catch (e) {
        this.$log.error(e);
        this.displayError(`Óvænt villa kom upp. Vinsamlegast reyndu aftur.`);
      } finally {
        this.workplaceTypesLoading = false;
      }
    },
    async loadWorkplaces() {
      this.workplacesLoading = true;
      try {
        const response = await core.dropdownWorkplaceList(this.search.workplaceType);
        this.workplaces = response.data;
        if (this.search.workplace && !this.workplaces.find(w => w.id === this.search.workplace)) {
          this.search.workplace = null;
        }
      } catch (e) {
        this.$log.error(e);
        this.displayError(`Óvænt villa kom upp. Vinsamlegast reyndu aftur.`);
      } finally {
        this.workplacesLoading = false;
      }
    },
    async loadPlacementGroups() {
      this.placementGroupsLoading = true;
      try {
        const response = await core.dropdownPlacementgroupFilterList(this.search.period, null, this.search.workplace);
        this.placementGroups = response.data;
        if (this.search.placementGroup && !this.placementGroups.find(p => p.id === this.search.placementGroup)) {
          this.search.placementGroup = null;
        }
      } catch (e) {
        this.$log.error(e);
        this.displayError(`Óvænt villa kom upp. Vinsamlegast reyndu aftur.`);
      } finally {
        this.placementGroupsLoading = false;
      }
    },
    onSearchChanged() {
      this.$emit('input', {
        placementGroup: this.search.placementGroup,
        period: this.search.period,
        periodPart: this.search.periodPart,
        workplace: this.search.workplace,
        workplaceType: this.search.workplaceType,
        exists: this.search.exists,
      });
    },
    ...mapActions({
      displayError: 'displayError',
      displaySuccess: 'displaySuccess',
    }),
  },
};
</script>
